import { KeyedMutator } from 'swr';
import { useClient } from '../../../hooks/useClient';
import { ValidationErrors } from '../../../hooks/useForm';
import { EditReservationPayload, Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { Organization } from '../../../types/organization';
import { User } from '../../../types/user';
import { ReservationForm } from '../validators';
import { ExternalUserWithCancellation, ReservationEvent } from '../types';
import { getToastMessages } from '../utils/messages';
import { mutateUserProfileData } from '../utils/cache';
import { getCreateReservationBody, getEditReservationBody } from '../utils/payloads';

/** An SWR `mutate` called with no data: it clears or revalidates the cached value. */
type Revalidate = (data?: undefined) => Promise<unknown>;

interface Options {
  resource: Reservation | undefined;
  isCreating: boolean;
  isEditing: boolean;
  reservationType: ReservationType | undefined;
  findReservationTypeByBranchAndName: (branchId?: string, name?: string) => ReservationType | undefined;
  isGroupReservation: boolean;
  isUnregisteredClient: boolean;
  organization: Organization | undefined;
  authUser: User | undefined;
  form: ReservationForm;
  validate: () => ValidationErrors<ReservationForm> | undefined;
  setIsSaving: (isSaving: boolean) => void;
  resetForm: () => void;
  initialTemporaryEvent: ReservationEvent | undefined;
  invalidHourSelection: boolean;
  canceledUsers: ExternalUserWithCancellation[];
  setCanceledUsers: (users: ExternalUserWithCancellation[]) => void;
  externalUserProfileId: string | undefined;
  mutateReservations: KeyedMutator<Reservation[] | undefined>;
  mutateExistingReservations: Revalidate;
  mutateOrgCoupons: Revalidate;
  mutateActiveUserCoupons: Revalidate;
  mutateSelectedUserCoupon: Revalidate;
  onSaveSuccess: (() => void) | undefined;
  /** Closes the panel. */
  onHide: () => void;
}

/** The requests of the panel that are not a cancellation: save, block, unblock and register attendance. */
export const useReservationRequests = ({
  resource,
  isCreating,
  isEditing,
  reservationType,
  findReservationTypeByBranchAndName,
  isGroupReservation,
  isUnregisteredClient,
  organization,
  authUser,
  form,
  validate,
  setIsSaving,
  resetForm,
  initialTemporaryEvent,
  invalidHourSelection,
  canceledUsers,
  setCanceledUsers,
  externalUserProfileId,
  mutateReservations,
  mutateExistingReservations,
  mutateOrgCoupons,
  mutateActiveUserCoupons,
  mutateSelectedUserCoupon,
  onSaveSuccess,
  onHide,
}: Options) => {
  const { post, patch, delete: deleteResource } = useClient();

  const handleSave = async (): Promise<boolean> => {
    const errors = validate();
    if (!form || !organization || errors || invalidHourSelection) return false;

    setIsSaving(true);

    const { branch } = form;
    const reservationTypeForSelectedBranch = findReservationTypeByBranchAndName(branch?.id, reservationType?.name);
    if (!reservationTypeForSelectedBranch) {
      setIsSaving(false);
      return false;
    }

    if (isEditing && resource) {
      const reservationTypeForEditedBranch = branch?.id
        ? findReservationTypeByBranchAndName(branch.id, reservationType?.name)
        : undefined;

      const editBody = getEditReservationBody(form, {
        isUnregisteredClient,
        canceledUsers,
        initialTemporaryEvent,
        reservationTypeForEditedBranch,
      });

      const { successMessage, errorMessages } = getToastMessages(true, isGroupReservation);
      const response = await patch<EditReservationPayload>(`/reservations/${resource.id}`, {
        body: editBody,
        successMessage,
        errorMessages,
      });

      if (response) {
        mutateOrgCoupons(undefined);
        mutateActiveUserCoupons();
        mutateSelectedUserCoupon(undefined);
        mutateExistingReservations(undefined);
        mutateReservations();
        if (externalUserProfileId) {
          mutateUserProfileData(organization, externalUserProfileId);
        }
        // Limpiar los usuarios cancelados después de guardar
        setCanceledUsers([]);
        onSaveSuccess?.();
      }

      setIsSaving(false);
      return !!response;
    }
    const createBody = getCreateReservationBody(form, {
      isUnregisteredClient,
      isCreating,
      reservationTypeForSelectedBranch,
    });

    const { successMessage, errorMessages } = getToastMessages(false, isGroupReservation);
    const response = await post<Reservation>('/reservations', {
      body: createBody,
      successMessage,
      errorMessages,
    });

    if (response) {
      mutateOrgCoupons(undefined);
      mutateActiveUserCoupons();
      mutateSelectedUserCoupon(undefined);
      mutateExistingReservations(undefined);
      mutateReservations();
      if (externalUserProfileId) {
        mutateUserProfileData(organization, externalUserProfileId);
      }
      resetForm();
      onSaveSuccess?.();
    }

    setIsSaving(false);
    return !!response;
  };

  const handleBlock = async (): Promise<boolean> => {
    if (!form || !organization) return false;

    const blockErrors = {
      branch: !form.branch ? 'Debes seleccionar una sucursal' : undefined,
      localStartDate: !form.localStartDate && !initialTemporaryEvent ? 'Debes seleccionar una hora de inicio' : undefined,
      localEndDate: !form.localEndDate && !initialTemporaryEvent ? 'Debes seleccionar una hora de fin' : undefined,
      selectedDay: !form.selectedDay ? 'Debes seleccionar un día' : undefined,
    };

    const hasBlockErrors = Object.values(blockErrors).some((error) => !!error);
    if (hasBlockErrors) {
      return false;
    }

    if (invalidHourSelection) {
      return false;
    }

    setIsSaving(true);

    const { branch, localStartDate, localEndDate } = form;

    const reservationTypeForSelectedBranch = findReservationTypeByBranchAndName(branch?.id, reservationType?.name);
    if (!reservationTypeForSelectedBranch) {
      setIsSaving(false);
      return false;
    }

    const createBody = {
      branchId: branch?.id,
      userId: authUser?.id,
      reservationTypeId: reservationTypeForSelectedBranch?.id,
      utcStartDate: localStartDate,
      utcEndDate: localEndDate,
      blocked: true,
    };

    const response = await post<Reservation>('/reservations', {
      body: createBody,
      successMessage: {
        summary: 'Horario bloqueado',
        detail: 'El horario se ha bloqueado correctamente',
      },
      errorMessages: {
        summary: 'Error al bloquear el horario',
        defaultDetail: 'No se ha podido bloquear el horario',
        RESERVATION_IS_IN_REST_HOUR: 'El horario seleccionado está en hora de descanso.',
        RESERVATION_IS_OUTSIDE_ALLOWED_HOURS: 'El horario seleccionado está fuera de las horas permitidas.',
      },
    });

    if (response) {
      mutateExistingReservations(undefined);
      mutateReservations();
    }

    setIsSaving(false);
    return !!response;
  };

  const handleUnblock = async (): Promise<boolean> => {
    if (!resource || !resource.id) return false;

    setIsSaving(true);

    const response = await deleteResource(`/reservations/${resource.id}`, {
      successMessage: {
        summary: 'Horario desbloqueado',
        detail: 'El horario se ha desbloqueado correctamente',
      },
      errorMessages: {
        summary: 'Error al desbloquear el horario',
        defaultDetail: 'No se ha podido desbloquear el horario',
      },
    });

    if (response) {
      mutateExistingReservations(undefined);
      mutateReservations();
      onHide();
    }

    setIsSaving(false);
    return !!response;
  };

  const handleAttendanceSave = async (): Promise<boolean> => {
    if (!resource || !organization || !form?.attendanceData) return false;

    setIsSaving(true);
    const payload = {
      reservationId: resource.id,
      attendanceData: Object.entries(form.attendanceData).map(([externalUserId, attendance]) => ({
        externalUserId,
        attendance,
      })),
    };

    const response = await post('/reservations/register-attendance', {
      body: payload,
      successMessage: {
        summary: 'Asistencias registradas',
        detail: 'Las asistencias se han registrado correctamente',
      },
      errorMessages: {
        summary: 'Error al registrar asistencias',
        defaultDetail: 'No se han podido registrar las asistencias',
      },
    });

    if (response) {
      mutateReservations();
      onHide();
      if (externalUserProfileId && organization) {
        mutateUserProfileData(organization, externalUserProfileId);
      }
    }

    setIsSaving(false);

    return !!response;
  };

  return { handleSave, handleBlock, handleUnblock, handleAttendanceSave };
};
